/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { extInfo } from 'lib/extInfo';
import { pickBy } from 'ramda';
import { useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import { IS_BIZ_USER, IS_BLOCKED, IS_PUBLIC_ALBUM, IS_PUBLIC_FOLDER } from 'reactApp/appHelpers/configHelpers';
import { isActivitiesFeatureEnabled } from 'reactApp/appHelpers/featuresHelpers';
import {
    familyFeatureDropdownButtons,
    familyFeatureDropdownPreviewFree,
    familyFeatureDropdownPreviewPaid,
} from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import {
    publicInfoAlbumToolbarEnabled,
    publicInfoFileToobarEnabled,
    publicInfoFolderToobarEnabled,
} from 'reactApp/appHelpers/featuresHelpers/features/publicInfo';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { isThemedSharedToobarItem, isThemedSharedToobarItemMore } from 'reactApp/appHelpers/featuresHelpers/features/themedPublic';
import { forceVigboLinkedAccount } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { CUSTOM_ALBUMS_ID } from 'reactApp/modules/albums/albums.constants';
import { isAddToAlbumsEnabled, isAlbumFolderCustom, isCreatePublicAlbumsEnabled } from 'reactApp/modules/albums/albums.selector';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { getAllDocumentsCategory } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { AllDocumentsCategoryType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { hasFolderFileWithFace } from 'reactApp/modules/faces/faces.selectors';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature } from 'reactApp/modules/family/family.selectors';
import { isDefaultRootContent } from 'reactApp/modules/home/home.selectors';
import {
    getPublicRootWeblink,
    isDownloadActionAvailable,
    isOwnPublic,
    isPublicUploadEnabled,
} from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import {
    isAutodeleteFolder,
    isDomainFolder,
    isFamilyFolder,
    isMountedFolder,
    isSharedFolder,
} from 'reactApp/modules/storage/folder.helpers';
import { STORAGE_CONFIG } from 'reactApp/modules/storage/storage.config.new';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    canMakeFamilyFolder,
    getStorageCurrentFolder,
    getStorageItemById,
    getStorageSelectedItems,
    hasParentMountedOrSharedFolder,
    isMountedOrSharedFolder,
    isReadOnlyMountedOrShared,
    isRootCurrentFolder,
} from 'reactApp/modules/storage/storage.selectors';
import { type CloudItem, EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { getCurrentGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { getCurrentVigboAlbum, getVigboLinkState, hasActiveVigboTariff } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import type { RootState } from 'reactApp/store';
import { UserQuotaGroupType } from 'reactApp/types/QuotaCleaner';
import { isItemsEmailUrlOk } from 'reactApp/ui/ContextMenu/createContextMenuList';

interface UseActionsConfig {
    showDisabled?: boolean;
    useOnlyCurrentFolder?: boolean;
    id?: string;
    storage?: EStorageType;
    skipUpload?: boolean;
    skipRename?: boolean;
    isMobile?: boolean;
}

export const useActionsConfig = ({
    showDisabled = false,
    useOnlyCurrentFolder = false,
    skipUpload = false,
    skipRename = false,
    storage,
    id,
    isMobile = false,
}: UseActionsConfig = {}): Partial<Record<EActions, boolean>> => {
    const actions: Partial<Record<EActions, boolean>> = {};
    const currentStorage = useSelector(getCurrentStorage);
    const currentCategory = useSelector(getAllDocumentsCategory);
    const isIncomingItem = currentCategory === AllDocumentsCategoryType.sharedWithYou;
    const storageForSettings = isIncomingItem ? EStorageType.incomingPublic : currentStorage;
    const settings = STORAGE_CONFIG[storage || storageForSettings];

    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);

    const {
        isHome,
        isPublic,
        isAttaches,
        isStock,
        isSharedIncoming,
        isSharedLinks,
        isSharedAutodelete,
        isAlbums,
        isPdfEdit,
        isQuotaCleaner,
        isSearch,
        isTrashBin,
        isVigbo,
    } = getStorage(storage ?? currentStorage);
    const storageItems = useSelector((state: RootState) => getStorageSelectedItems(state, storage ?? currentStorage)) as CloudItem[];
    const itemById = useSelector((state: RootState) => {
        return getStorageItemById(state, storage ?? currentStorage, id);
    });
    const items = useOnlyCurrentFolder ? [] : itemById ? [itemById] : storageItems;
    const isSelectedMounted = items.some(isMountedFolder);
    const selectedCount = items.length;
    const oneItem = selectedCount === 1;
    const isOneItemFolder = oneItem && items[0].isFolder;
    const itemsWithWeblink = items.some((item) => 'weblink' in item && item?.weblink);
    const itemsCloudAttaches = isAttaches && items.some((item) => 'attachType' in item && item?.attachType === EAttachTypes.cloud);
    const currentFolder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage ?? currentStorage));
    const isOwn = useSelector(isOwnPublic);
    const isMountedOrSharedFolderStatus = useSelector((state: RootState) => isMountedOrSharedFolder(state, currentFolder));
    const isCurrentSharedOrMounted = isMountedOrSharedFolderStatus.is;
    const isReadOnly = useSelector((state: RootState) => isReadOnlyMountedOrShared(state, currentFolder));
    const isUploadEnabled = useSelector(isPublicUploadEnabled);
    const isDownloadEnabled = useSelector(isDownloadActionAvailable);
    const itemsOrCurrentFolder = items.length ? items : [currentFolder];
    const isPublicAlbumEnabled = useSelector((state: RootState) => isCreatePublicAlbumsEnabled(state, itemsOrCurrentFolder));
    const isItemAlbum = (oneItem && items.every(isAlbum)) || (selectedCount === 0 && isAlbum(currentFolder));
    const { weblink = '' } = useSelector(getPublicRootWeblink) || {};
    const hasFaces = useSelector((state: RootState) => hasFolderFileWithFace(state, weblink));
    const isAlbumCustomFolder = useSelector(isAlbumFolderCustom);
    const isEmptyFolder = (currentFolder?.childs?.length || 0) === 0;
    const isDefaultContent = useSelector(isDefaultRootContent);
    const isSelectingMode = useSelector(SelectionsSelectors.isAnyIndexSelected);
    const isNotRootFolder = Boolean(currentFolder) && currentFolder?.id !== ROOT_FOLDER_ID;
    const itemsWithDomain = items.length ? items.some(isDomainFolder) : currentFolder && isDomainFolder(currentFolder);
    const hasParentMountedOrShared = useSelector((state: RootState) => hasParentMountedOrSharedFolder(state, items));
    const activeDownloadInFavouritesForBizUser = IS_BIZ_USER && (storage ?? currentStorage) === EStorageType.favorites;
    const isRootFolder = useSelector((state: RootState) => isRootCurrentFolder(state, currentStorage));
    const isAlbumsRootFolder = isAlbums && isRootFolder;
    const currentGroup = useSelector(getCurrentGroup);
    const {
        isDomainAndReadRestricted,
        isDomainAndDownloadRestricted,
        isDomainAndEditRestricted,
        isDomainAndCreateRestricted,
        isDomainAndShareRestricted,
        isDomainAndDeleteRestricted,
    } = getDomainRestrictionsForItems(isTrashBin ? currentFolder : itemsOrCurrentFolder);
    const hasB2BRightsToMove = !isDomainAndDeleteRestricted || !isDomainAndCreateRestricted;
    const hasB2BRightsToSeeHistory = !(isDomainAndReadRestricted && isDomainAndEditRestricted);

    const isVigboActiveTariff = useSelector(hasActiveVigboTariff);
    const vigboAlbumsExts = extInfo.getExtsForVigboAlbums();
    const currentVigboAlbum = useSelector(getCurrentVigboAlbum);
    const isVigboLinkedAccount = useSelector(getVigboLinkState) === VigboAccountLinkState.linked || forceVigboLinkedAccount;

    const isAddToVigboAlbumAvailable =
        storage !== EStorageType.albums &&
        isVigboActiveTariff &&
        isVigboLinkedAccount &&
        /**
         * Старые версии TS не умеют работает с reduce, every и filter в массивах из Union'ов, поэтому кастуем
         * @link https://github.com/microsoft/TypeScript/issues/44373
         */
        ((currentStorage !== EStorageType.vigbo &&
            items.length > 0 &&
            (items as any[]).every((item) => Boolean(item) && 'ext' in item && vigboAlbumsExts.includes(item.ext))) ||
            (currentStorage === EStorageType.vigbo && currentVigboAlbum && items.length === 0));

    const isAddToAlbumsAvailable = useSelector((state: RootState) => isAddToAlbumsEnabled(state, itemsOrCurrentFolder));

    if (settings?.facesFilter) {
        actions[EActions.facesFilter] = hasFaces && Boolean(IS_PUBLIC_FOLDER);
    }

    if (settings?.back && selectedCount && !isAlbumCustomFolder && !isAlbumsRootFolder) {
        actions[EActions.back] = true;
    }

    if (settings?.selectAll) {
        let selectingAvailable = Boolean(currentFolder);

        if (isSharedAutodelete) {
            selectingAvailable = !isEmptyFolder;
        }

        if (isAlbums) {
            selectingAvailable = selectingAvailable && currentFolder?.id !== CUSTOM_ALBUMS_ID;
        }

        actions[EActions.selectAll] = selectingAvailable;
    }

    if (settings?.restoreAll && !isBizUser) {
        actions[EActions.restoreAll] = !selectedCount && !isEmptyFolder;
    }

    if (settings?.restore && !isDomainAndDeleteRestricted) {
        actions[EActions.restore] = Boolean(selectedCount);
    }

    if (settings?.clear && !isDomainAndDeleteRestricted) {
        actions[EActions.clear] = !isEmptyFolder;
    }

    if (settings?.createFolder && !isDomainAndCreateRestricted) {
        actions[EActions.createFolder] = !isReadOnly;
    }

    if (settings?.publish && !IS_BLOCKED && !isDomainAndShareRestricted) {
        const canPublishAlbum = !isVigbo && !isAlbums && isPublicAlbumEnabled;
        let publishAvailable = (oneItem && !canPublishAlbum) || (isPdfEdit && !!itemById);

        if (isHome) {
            publishAvailable = publishAvailable || (!selectedCount && isNotRootFolder);
        }

        if (isAlbums) {
            publishAvailable = isItemAlbum && !isAlbumCustomFolder && isPublicAlbumEnabled && !isAlbumsRootFolder;
        }

        if (isVigbo) {
            publishAvailable = !isSelectingMode && Boolean(currentFolder && 'weblink' in currentFolder && currentFolder.weblink);
        }

        if (isPublicAlbumEnabled) {
            actions[EActions.publishAlbum] = true;
        }

        if (itemsCloudAttaches) {
            publishAvailable = false;
        }

        // CLOUD-4594: share available only for non-family folders
        actions[EActions.publish] = publishAvailable;

        if (
            (IS_BIZ_USER && selectedCount >= 1 && itemsWithWeblink) ||
            (!publishAvailable && itemsWithWeblink && !isAlbumsRootFolder && !isAlbumCustomFolder)
        ) {
            actions[EActions.unpublish] = true;
        }

        if (isVigbo) {
            actions[EActions.setupVigboGallery] = true;
            actions[EActions.watchVigboGallery] = true;
        }
    }

    if (settings?.addToVigboAlbum && isAddToVigboAlbumAvailable) {
        actions[EActions.addToVigboAlbum] = true;
    }

    if (settings?.addToCustomAlbum && isAddToAlbumsAvailable && !isAlbumCustomFolder && !isAlbumsRootFolder) {
        actions[EActions.addToCustomAlbum] = true;
    }

    if (settings?.share && !isDomainAndShareRestricted) {
        let shareAvailable = isOneItemFolder && !itemsWithDomain;

        if (isHome && isNotRootFolder) {
            shareAvailable =
                !itemsWithDomain &&
                (isCurrentSharedOrMounted
                    ? // нельзя "Настроить доступ" у подпапок примонтированной папки
                      !selectedCount && isMountedOrSharedFolderStatus.folderId === currentFolder?.id
                    : !selectedCount || isOneItemFolder);
        } else if (isSharedLinks) {
            shareAvailable = isOneItemFolder;
        } else if (isSharedIncoming) {
            shareAvailable = isOneItemFolder && isSelectedMounted;
        } else if (isSearch) {
            shareAvailable = !hasParentMountedOrShared && isOneItemFolder;
        }

        // CLOUD-4594: share available only for non-family folders
        actions[EActions.share] = shareAvailable && (currentFolder as any)?.kind !== 'family' && oneItem && items[0]?.kind !== 'family';
    }

    if (settings?.shareLink && !isDomainAndShareRestricted) {
        // CLOUD-4594: share available only for non-family folders
        actions[EActions.shareLink] =
            (oneItem || !selectedCount) && (currentFolder as any)?.kind !== 'family' && oneItem && items[0]?.kind !== 'family';
    }

    if (settings?.clone && !isOwn && !itemsCloudAttaches && !IS_BLOCKED && !isDomainAndCreateRestricted) {
        actions[EActions.clone] = isAttaches ? selectedCount > 0 : true;
    }

    if ((settings?.download || activeDownloadInFavouritesForBizUser) && !isDomainAndDownloadRestricted) {
        let downloadAvailable = !!selectedCount;

        if (isAttaches) {
            downloadAvailable = Boolean(selectedCount);
        } else if (isHome) {
            downloadAvailable = (!isEmptyFolder && !isDefaultContent && !isRootFolder) || (isRootFolder && !!selectedCount);
        } else if (isPublic) {
            downloadAvailable = isDownloadEnabled && !isEmptyFolder;
        } else if (isStock) {
            downloadAvailable = !isEmptyFolder;
        } else if (isSharedIncoming) {
            downloadAvailable = isSelectedMounted;
        } else if (isAlbums) {
            downloadAvailable = !isAlbumCustomFolder && !isAlbumsRootFolder && !isEmptyFolder;
        } else if (isVigbo) {
            downloadAvailable = true;
        } else if (isQuotaCleaner) {
            if (selectedCount > 1 && currentGroup?.groupType === UserQuotaGroupType.Letter) {
                downloadAvailable = false;
            } else {
                downloadAvailable = true;
            }
        }

        actions[EActions.download] = downloadAvailable;
    }

    if (settings?.sendViaMail && !isDomainAndShareRestricted) {
        actions[EActions.sendViaMail] = Boolean(selectedCount) && isItemsEmailUrlOk(items);
    }

    if (settings?.add && !isDomainAndCreateRestricted) {
        actions[EActions.upload] = ((isPublic && (isOwn || isUploadEnabled)) || !isPublic) && !skipUpload;
    }

    if (settings?.remove && !isDomainAndDeleteRestricted) {
        let removeAvailable = Boolean(selectedCount) && !itemsWithDomain;

        if (isHome) {
            removeAvailable =
                !itemsWithDomain &&
                ((selectedCount && !isReadOnly) ||
                    (!selectedCount && !isReadOnly && isNotRootFolder) ||
                    (!selectedCount && isReadOnly && isCurrentSharedOrMounted));
        } else if (isPublic) {
            if (isMobile) {
                removeAvailable = IS_PUBLIC_ALBUM
                    ? removeAvailable && isOwn && !isItemAlbum
                    : (!itemById && Boolean(selectedCount) && isOwn) ||
                      (Boolean(itemById) && !itemById?.isFolder && (isOwn || isUploadEnabled));
            } else {
                removeAvailable = IS_PUBLIC_ALBUM ? removeAvailable && isOwn : removeAvailable && isUploadEnabled && !isAnonymous;
            }
        } else if (isSharedIncoming) {
            removeAvailable = removeAvailable && isOneItemFolder;
        } else if (isAlbums) {
            removeAvailable = !isAlbumCustomFolder && !isAlbumsRootFolder;
        } else if (isVigbo) {
            removeAvailable = isVigboActiveTariff || !isSelectingMode;
        }

        actions[EActions.remove] = removeAvailable;
    }

    if (settings?.favorites && !isMountedOrSharedFolderStatus.isMounted) {
        const allInFavorites = (items as CloudItem[]).every((item) => 'isInFavorites' in item && item.isInFavorites);
        const mountedItems = (items as CloudItem[]).filter((item) => 'isMounted' in item && item.isMounted && !item.isFolder);

        actions[EActions.addToFavorites] =
            isAlbumCustomFolder || isAlbumsRootFolder ? false : !allInFavorites && !mountedItems.length && !hasParentMountedOrShared;
        actions[EActions.removeFromFavorites] =
            isAlbumCustomFolder || isAlbumsRootFolder ? false : Boolean(selectedCount) && allInFavorites;
    }

    if (settings?.mount && !isDomainAndShareRestricted) {
        actions[EActions.mount] = !isSelectedMounted && oneItem;
    }

    if (settings?.copy && !itemsWithDomain && !isDomainAndCreateRestricted) {
        let copyAvailable = Boolean(selectedCount);

        if (isHome) {
            copyAvailable = copyAvailable || isNotRootFolder;
        }

        actions[EActions.copy] = copyAvailable;
    }

    if (settings?.move && hasB2BRightsToMove) {
        let moveAvailable = Boolean(selectedCount) && !isReadOnly && !itemsWithDomain;

        if (isHome) {
            moveAvailable =
                moveAvailable ||
                (!selectedCount && ((isNotRootFolder && !itemsWithDomain && !isReadOnly) || (isReadOnly && isCurrentSharedOrMounted)));
        }

        actions[EActions.move] = moveAvailable;
    }

    if (settings?.rename && !isDomainAndEditRestricted) {
        let renameAvailable = oneItem && !isReadOnly && !itemsWithDomain;

        if (isHome) {
            renameAvailable =
                renameAvailable ||
                (!selectedCount && ((isNotRootFolder && !itemsWithDomain && !isReadOnly) || (isReadOnly && isCurrentSharedOrMounted)));
        }

        if (isAlbums) {
            renameAvailable = isItemAlbum && !isAlbumCustomFolder && !isAlbumsRootFolder;
        }

        if (isVigbo) {
            renameAvailable = !!currentFolder && !isSelectingMode && isVigboActiveTariff;
        }

        actions[EActions.rename] = renameAvailable && !skipRename;
    }

    const hasFamilyFoldersFeature = useSelector(getFamilyFoldersFeature);
    const hasFamilyAlbumsFeature = useSelector(getFamilyAlbumsFeature);
    const isPaid = useSelector(UserSelectors.isPaidUser);
    const isCanMakeFamilyFolder = useSelector((state: RootState) => canMakeFamilyFolder(state, items[0]));

    if (
        settings?.makeFamilyFolder &&
        ((oneItem && items[0]?.isFolder) ||
            (!isMountedFolder(currentFolder) && !isFamilyFolder(currentFolder) && !isSharedFolder(currentFolder)))
    ) {
        const showFamilyFeature =
            (hasFamilyFoldersFeature && hasFamilyAlbumsFeature && familyFeatureDropdownButtons) ||
            (familyFeatureDropdownPreviewFree && !isPaid) ||
            (familyFeatureDropdownPreviewPaid && isPaid);

        actions[EActions.makeFamilyFolder] =
            showFamilyFeature && isCanMakeFamilyFolder && !isSharedIncoming && !isSharedLinks && !isAutodeleteFolder(items?.[0]);
    }

    if (settings?.makeFamilyFolderCommon && oneItem && items[0]?.isFolder) {
        const showFamilyFeature =
            (hasFamilyFoldersFeature && hasFamilyAlbumsFeature && familyFeatureDropdownButtons) ||
            (familyFeatureDropdownPreviewFree && !isPaid) ||
            (familyFeatureDropdownPreviewPaid && isPaid);

        actions[EActions.makeFamilyFolderCommon] = showFamilyFeature && items[0].kind === 'family';
    }

    if (settings?.history && hasB2BRightsToSeeHistory) {
        actions[EActions.history] = oneItem && ((!isOneItemFolder && !isReadOnly) || isActivitiesFeatureEnabled);
    }

    if (isAlbumCustomFolder || isAlbumsRootFolder) {
        actions[EActions.createAlbum] = true;
    }

    // tempexp_17340-start
    if (settings?.changeDesign) {
        actions[EActions.changeDesign] =
            isThemedSharedToobarItem && !selectedCount && Boolean(currentFolder && 'weblink' in currentFolder && currentFolder?.weblink);
    }

    if (settings?.changeDesignMore) {
        actions[EActions.changeDesignMore] =
            isThemedSharedToobarItemMore &&
            !selectedCount &&
            Boolean(currentFolder && 'weblink' in currentFolder && currentFolder?.weblink);
    }
    // tempexp_17340-end

    // tempexp_18662-start
    if (settings?.publicInfo) {
        const showForPublicAlbum =
            isItemAlbum && Boolean(currentFolder && 'weblink' in currentFolder && currentFolder?.weblink) && publicInfoAlbumToolbarEnabled;

        const selectedFolderIsPublic = isOneItemFolder && items[0]?.weblink;
        const showForPublicFolder =
            publicInfoFolderToobarEnabled &&
            ((!selectedCount && Boolean(currentFolder && 'weblink' in currentFolder && currentFolder?.weblink)) || selectedFolderIsPublic);

        const showForPublicFile = oneItem && items[0]?.weblink && !items[0]?.isFolder && publicInfoFileToobarEnabled;

        actions[EActions.publicInfo] = showForPublicAlbum || showForPublicFolder || showForPublicFile;
    }
    // tempexp_18662-end

    if (settings?.complain && isPublic && seoEnable && !IS_BIZ_USER) {
        actions[EActions.complain] = true;
    }

    return showDisabled ? actions : pickBy(Boolean, actions);
};
